import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Users, CalendarCheck, AirplaneTilt, Money } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { CelebrationsBoard } from "../../components/admin/dashboard/CelebrationsBoard";
import { ApprovalsCard, PendingLeavesCard } from "../../components/admin/dashboard/DashboardCards";

const AXIS_TICK = { fontSize: 12 };
const TOOLTIP_CURSOR = { fill: "#FAFAF9" };
const BAR_RADIUS = [6, 6, 0, 0];

const Stat = ({ icon: Icon, label, value, tint, testId }) => (
  <div data-testid={testId} className="bg-white border border-stone-200 rounded-lg p-6 hover:shadow-sm transition-shadow duration-200">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${tint}`}><Icon size={22} weight="duotone" /></div>
    <div className="text-3xl font-heading font-extrabold tabular-nums">{value}</div>
    <div className="text-sm text-stone-500 mt-1">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/admin").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="text-stone-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="text-stone-500 text-sm mt-1">Workforce overview for {d.current_month}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
        <Stat icon={Users} label="Active employees" value={d.total_employees} tint="bg-orange-50 text-primary" testId="stat-total-employees" />
        <Stat icon={CalendarCheck} label="Present today" value={d.present_today} tint="bg-green-50 text-green-700" testId="stat-present-today" />
        <Stat icon={AirplaneTilt} label="On leave today" value={d.on_leave_today} tint="bg-amber-50 text-amber-700" testId="stat-on-leave" />
        <Stat icon={Money} label={`Payroll ${d.current_month}`} value={d.payroll_status} tint="bg-stone-100 text-secondary" testId="stat-payroll-status" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-heading font-bold mb-4">Attendance — last 7 days</h3>
          <div className="h-56" style={{ minHeight: 224 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.weekly_attendance}>
                <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="present" fill="#C84C09" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <ApprovalsCard pendingLeaves={d.pending_leaves} pendingRegs={d.pending_regularizations} departments={d.department_counts} />
      </div>

      <CelebrationsBoard birthdays={d.upcoming_birthdays} anniversaries={d.upcoming_anniversaries} />

      <PendingLeavesCard leaves={d.recent_pending_leaves} />
    </div>
  );
}
